import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LookupService } from '../../../services/lookup.service';
import { LookupValueItem } from '../../../interfaces/lookup.interface';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import {
  NzFormControlComponent,
  NzFormDirective,
  NzFormItemComponent,
} from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CommonModule, DatePipe } from '@angular/common';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { BillingService } from '../../../services/billing.service';
import { MONTHS } from '../../../shared/constants/static-data.constants';
import { NzInputModule } from 'ng-zorro-antd/input';
import { UserService } from '../../../services/user.service';
import { DropdownSelectComponent } from '../../../shared/components/dropdown-select/dropdown-select.component';

@Component({
  selector: 'ppl-billing-filter',
  standalone: true,
  imports: [
    ButtonComponent,
    NzColDirective,
    NzFormControlComponent,
    NzFormDirective,
    NzFormItemComponent,
    NzOptionComponent,
    NzRowDirective,
    NzSelectComponent,
    ReactiveFormsModule,
    NzAutocompleteModule,
    CommonModule,
    NzDatePickerModule,
    NzInputModule,
    DropdownSelectComponent,
  ],
  templateUrl: './billing-filter.component.html',
  styleUrl: './billing-filter.component.css',
})
export class BillingFilterComponent implements OnInit {
  @Input() filterFields: any[] = [];
  @Input() billingPageName: string = '';
  @Input() billingScreen: string = '';
  @Output() searchEvent = new EventEmitter<any>();
  statusList: LookupValueItem[] = [];
  claimStatusList: LookupValueItem[] = [];
  statusCodeList: LookupValueItem[] = [];
  pageId = '';
  tabId = '';
  programName!: string;
  batchIds: any[] = [];
  fileNames: any[] = [];
  filteredConsumers: any[] = [];
  filteredProviders: any[] = [];
  billingFilterForm!: FormGroup;
  radioOptions: LookupValueItem[] = [];
  eventCodeOptions: LookupValueItem[] = [];
  mcoEntries: any[] = [];
  years: any[] = [{label:'2025', value: '2025'}, {label:'2026', value: '2026'}];
  months: any[] = MONTHS;
  fileTypeList = [{label:'CDR', value: 'CDR'}, {label:'Payment', value: 'Payment'}];
  fileTypes = [{label:'CDR', value: 'CDR'}, {label:'Payment', value: 'Payment'}];
  workdayFileType = [{label:'WORKDAY_INVOICE', value: 'WORKDAY_INVOICE'}, {label:'WORKDAY_PAYMENT', value: 'WORKDAY_PAYMENT'}];
  ignoreReasonList: any[] = [];
  batchDates: any;

  loopback = [
    { label: 'Last 30 days', value: '30 days' },
    { label: 'Last 60 days', value: '60 days' },
    { label: 'Last 90 days', value: '90 days' },
    { label: 'Last 120 days', value: '120 days' },
    { label: 'Last 150 days', value: '150 days' },
    { label: 'Last 180 days', value: '180 days' },
  ];

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private lookupService: LookupService,
    private billingService: BillingService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.fileTypeList =
      this.billingScreen === 'workday' ? this.workdayFileType : this.fileTypes;
    const formFields = this.filterFields.reduce((acc, f) => {
      if (f.show) {
        acc = { ...acc, [f.field]: [f.default ? f.default : null] };
      }
      return acc;
    }, {});
    this.billingFilterForm = this.fb.group(formFields);

    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.statusList = this.lookupService.getLookupValue('claim_status_lkp');
    this.statusCodeList = this.lookupService
      .getLookupValue('claim_status_code')
      .sort((a: any, b: any) => {
        if (a.lkpValueCode !== b.lkpValueCode) {
          return a.lkpValueCode - b.lkpValueCode;
        }
        return a.lkpValueName.localeCompare(b.lkpValueName);
      }).map((item: any) => ({
        lkpValueCode: item.lkpValueCode,
        lkpValueName: `${item.lkpValueCode} - ${item.lkpValueName}`
      }));
    this.claimStatusList = this.lookupService.getLookupValue(
      'claim_payment_status'
    );
    this.radioOptions = this.lookupService.getLookupValue('Yes_No');
    const eventCodeList = this.lookupService.getLookupValue(
      'waystar_cdr_event_code_lkp'
    );
    this.eventCodeOptions = [...eventCodeList].sort((a: any, b: any) => {
      if (a.lkpValueCode !== b.lkpValueCode) {
        return a.lkpValueCode.localeCompare(b.lkpValueCode);
      }
      return a.lkpValueName.localeCompare(b.lkpValueName);
    }).map((item: any) => ({
      lkpValueCode: item.lkpValueCode,
      lkpValueName: `${item.lkpValueCode} - ${item.lkpValueName}`
    }));
    this.getDropdownValues();
  }

  getDropdownValues() {
    this.billingService
      .getIgnoreReasonDropdown(this.programName)
      .subscribe((data: any) => {
        this.ignoreReasonList = data?.responsedata;
      });
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        const mcoList = data.responsedata.filter(
          (item: any) => item.orgTypeLookup === 'MCO'
        );
        if (this.billingPageName === 'pmpmClaims') {
          const allowedOrgNames = [
            'ArchCare',
            'Catholic Health',
            'Centerlight',
            'Complete Senior Care',
            'ElderOne',
            'Fallon Health',
            'New York State Medicaid',
            'Senior Care Connection',
            'Total Senior Care',
          ];

          this.mcoEntries = mcoList.filter((item: any) =>
            allowedOrgNames.includes(item.orgName)
          );
          // this.mcoEntries = mcoList.filter((item: any) =>
          //   item.orgName === "New York State Medicaid" || item.orgName === "Pace CNY"
          // );
        } else {
          this.mcoEntries = mcoList;
        }
      }
    });
    this.billingService
      .getBatchDates(this.programName)
      .subscribe((data: any) => {
        const dates = data.responsedata.date;
        this.batchDates = dates.map((d: string) => ({
          label: d,
          value: d,
        }));
      });
  }

  onInput(e: Event, fieldName: string): void {
    const value = (e.target as HTMLInputElement).value;
    console.log('on inout-----', e, fieldName, value);
    // if (fieldName === 'consumer') {
    //   this.filteredConsumers = value ? this.consumers.filter(c => c.name.toLowerCase().includes(value.toLowerCase())) : [];
    // }
  }

  search() {
    const filters = this.getFilters();
    this.searchEvent.emit(filters);
  }

  getDate(timeframe: string) {
    const days = parseInt(timeframe.split(' ')[0], 10);
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - days);
    const formattedDate = `${(currentDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${currentDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${currentDate.getFullYear()}`;
    return formattedDate;
  }

  getFilters() {
    const filters = [];
    const event = this.billingFilterForm.value;
    let monthYear;
    if (event.month && event.year) {
      monthYear = `${event.month}${event.year}`;
    } else if (event.year) {
      monthYear = `${event.year}`;
    } else if (event.month) {
      monthYear = `${event.month}`;
    } else {
      monthYear = null;
    }

    if (event.timeframe) {
      filters.push({
        jsonPath: 'createddate',
        value: this.getDate(event.timeframe),
        operator: 'greaterthan',
      });
    }

    if (event.pplIcn && event.pplIcn.trim()) {
      filters.push({
        jsonPath: 'pplIcn',
        value: event.pplIcn.trim(),
        operator: 'Equal',
      });
    }

    if (event.extIcn && event.extIcn.trim()) {
      filters.push({
        jsonPath: 'externalIcn',
        value: event.extIcn.trim(),
        operator: 'Equal',
      });
    }

    if (event.pplBatchId && event.pplBatchId.trim()) {
      filters.push({
        jsonPath: 'lineItemControlNumber',
        value: event.pplBatchId.trim(),
        operator: 'Equal',
      });
    }

    if (event.memberId && event.memberId.trim()) {
      filters.push({
        jsonPath: 'memberId',
        value: event.memberId.trim(),
        operator: 'Equal',
      });
    } 
     if (event.paymentNumber && event.paymentNumber.trim()) {
      filters.push({
        jsonPath: 'paymentNumber',
        value: event.paymentNumber.trim(),
        operator: 'Equal',
      });
    } 

    if (event.batchName && event.batchName.trim()) {
      filters.push({
        jsonPath: 'batchName',
        value: event.batchName.trim(),
        operator: 'Equal',
      });
    }

    if (event.fileName && event.fileName.trim()) {
      filters.push({
        jsonPath: 'fileName',
        value: event.fileName.trim(),
        operator: 'contains',
      });
    }

    if (event.workdaySales && event.workdaySales.trim()) {
      filters.push({
        jsonPath: 'workdaySales',
        value: event.workdaySales.trim(),
        operator: 'contains',
      });
    }

    if (event.batchDate) {
      filters.push({
        jsonPath: 'batchCreatedDate',
        value: event.batchDate,
        operator: 'Equal',
      });
    }

    if (event.runId && event.runId.trim()) {
      filters.push({
        jsonPath: 'runId',
        value: event.runId.trim(),
        operator: 'Equal',
      });
    }

    if (event.claimStatus) {
      filters.push({
        jsonPath: 'paymentClaimStatus',
        value: event.claimStatus,
        operator: 'Equal',
      });
    }

    if (event.fileType) {
      filters.push({
        jsonPath: 'batchFileTypeLkp',
        value: event.fileType,
        operator: 'Equal',
      });
    }

    if (event.participantPplId && event.participantPplId.trim()) {
      filters.push({
        jsonPath: 'participantPplId',
        value: event.participantPplId.trim(),
        operator: 'Equal',
      });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({
        jsonPath: 'providerPplId',
        value: event.providerPplId.trim(),
        operator: 'Equal',
      });
    }

    if (event.authNumber && event.authNumber.trim()) {
      filters.push({
        jsonPath: 'authNumber',
        value: event.authNumber.trim(),
        operator: 'Equal',
      });
    }

    if (event.medicaidCin && event.medicaidCin.trim()) {
      filters.push({
        jsonPath: 'medicaidParticipantId',
        value: event.medicaidCin.trim(),
        operator: 'Equal',
      });
    }

    if (event.timesheetId && event.timesheetId.trim()) {
      filters.push({
        jsonPath: 'timesheetId',
        value: event.timesheetId.trim(),
        operator: 'contains',
      });
    }

    if (event.resub) {
      filters.push({
        jsonPath: 'resubFlg',
        value: event.resub,
        operator: 'Equal',
      });
    }

    if (event.serviceDate) {
      filters.push({
        jsonPath: 'serviceDate',
        value: this.datePipe.transform(event.serviceDate, 'MM/dd/yyyy'),
        operator: 'Equal',
      });
    }

    if (event.pmpm && event.pmpm.trim()) {
      filters.push({
        jsonPath: 'pmpmFlg',
        value: event.pmpm,
        operator: 'Equal',
      });
    }

    if (event.mcoName && event.mcoName.trim()) {
      filters.push({
        jsonPath: 'mcoName',
        value: event.mcoName.trim(),
        operator: 'Equal',
      });
    }
    if (event.ignoreReason && event.ignoreReason.trim()) {
      filters.push({
        jsonPath: 'ignoreReason',
        value: event.ignoreReason.trim(),
        operator: 'Equal',
      });
    }
    if (event.serviceCode && event.serviceCode.trim()) {
      filters.push({
        jsonPath: 'serviceCode',
        value: event.serviceCode.trim(),
        operator: 'Equal',
      });
    }
    if (event.modifier && event.modifier.trim()) {
      filters.push({
        jsonPath: 'modifierCode',
        value: event.modifier.trim(),
        operator: 'Equal',
      });
    }
    if (event.workdayRunId && event.workdayRunId.trim()) {
      filters.push({
        jsonPath: 'workdayRunId',
        value: event.workdayRunId.trim(),
        operator: 'Equal',
      });
    }
    if (event.payerId && event.payerId.trim()) {
      filters.push({
        jsonPath: 'payerId',
        value: event.payerId.trim(),
        operator: 'Equal',
      });
    }
    if (event.enrichedBatchId && event.enrichedBatchId.trim()) {
      filters.push({
        jsonPath: 'enrichedBatchId',
        value: event.enrichedBatchId.trim(),
        operator: 'Equal',
      });
    }
    if (event.month && event.month.trim()) {
      filters.push({
        jsonPath: 'pmpmMonth',
        value: event.month.trim(),
        operator: 'contains',
      });
    }
    if (event.year && event.year.trim()) {
      filters.push({
        jsonPath: 'pmpmYear',
        value: event.year.trim(),
        operator: 'contains',
      });
    }

    if (event.eventCode) {
      filters.push({
        jsonPath: 'eventCode',
        value: event.eventCode,
        operator: 'Equal',
      });
    }
    if (event.status) {
      filters.push({
        jsonPath: 'status',
        value: event.status,
        operator: 'Equal',
      });
    }
    if (event.statusCode) {
      filters.push({
        jsonPath: 'claimStatusCode',
        value: event.statusCode,
        operator: 'Equal',
      });
    }

    return filters;
  }
}
